import { Paper, Typography } from "matsuri-ui"
import { css } from "@emotion/react"
import { useMemo } from "react"
import dayjs from "dayjs"
import React from "react"
import { theme } from "../../theme"
import { Shift, getShiftCurrentTime } from "../../model/shift"
import { MobileMenu } from "@/templates/MobileMenu"
import { CompanyNotificationBanner } from "@/components/CompanyNotifcationBanner/CompanyNotifcationBanner"
import { useTranslation } from "@/hooks/useTranslation"

interface ShiftContextState {
    // keyはYYYYMMDD
    shiftMap?: Record<string, Shift[]>
    selectedDate: dayjs.Dayjs
    // EditModalのために入れているが、Editが別ページになれば不要
    refetch?: () => void
}

const ShiftContext = React.createContext<ShiftContextState | undefined>(
    undefined
)

export const useShiftContext = () => {
    const ctx = React.useContext(ShiftContext)
    if (!ctx) {
        throw new Error("useShiftContext must be used within ShiftProvider")
    }

    return ctx
}

const PlainCell = ({ date }: { date?: dayjs.Dayjs }) => {
    return (
        <div
            css={css`
                aspect-ratio: 1;
                padding: 6px 0;
            `}
        >
            <Typography>{date?.date()}</Typography>
        </div>
    )
}

const clickableCellStyle = css`
    display: grid;
    align-items: stretch;
    width: 100%;
    height: 100%;
    aspect-ratio: 1;
    overflow: hidden;
    color: ${theme.palette.intentions.text.primary};
    cursor: pointer;
`

const RenderShiftCell = ({
    date,
    disableScheduledColor
}: {
    date: dayjs.Dayjs
    disableScheduledColor?: boolean
}) => {
    const { shiftMap, selectedDate } = useShiftContext()
    const shifts = shiftMap?.[date.format("YYYYMMDD")]
    const earliestShift = shifts?.[0]
        ? shifts?.reduce(
              (acc, current) =>
                  dayjs
                      .unix(getShiftCurrentTime(current).start)
                      .isBefore(dayjs.unix(getShiftCurrentTime(acc).start))
                      ? current
                      : acc,
              shifts[0]
          )
        : undefined

    const hasScheduled = shifts?.some(shift => shift.status === "Scheduled")
    const hasCancelled = shifts?.some(shift => shift.status === "Cancelled")
    const hasDraftOrSubmitted = shifts?.some(
        shift => shift.status === "Draft" || shift.status === "Submitted"
    )

    return (
        <div
            css={[
                css`
                    display: grid;
                    gap: 2px;
                    padding: 2px 0;
                    cursor: pointer;
                `,
                date.format("YYYYMMDD") === selectedDate.format("YYYYMMDD") &&
                    css`
                        background-color: ${
                            theme.palette.intentions.cleanerGray.light
                        };
                    `
            ]}
        >
            <div
                css={css`
                    display: grid;
                    justify-content: center;
                `}
            >
                <Typography
                    css={[
                        css`
                            width: 24px;
                            height: 24px;
                            line-height: 24px;
                            border-radius: 50%;
                        `,
                        hasScheduled
                            ? css`
                                  font-weight: 700;
                                  color: white;
                                  background-color: ${
                                      disableScheduledColor
                                          ? theme.palette.intentions.cleanerGray
                                                .main
                                          : theme.palette.intentions.primary
                                                .main
                                  };
                              `
                            : hasCancelled
                              ? css``
                              : hasDraftOrSubmitted &&
                                css`
                                    background-color: ${
                                        theme.palette.intentions.cleanerGray
                                            .main
                                    };
                                `
                    ]}
                >
                    {date?.date()}
                </Typography>
            </div>

            {earliestShift && (
                <Typography
                    css={[
                        css`
                            font-size: 10px;
                            line-height: 1;
                        `,
                        !hasScheduled &&
                            hasCancelled &&
                            css`
                                color: #acacac;
                                text-decoration: line-through;
                            `,
                        !hasScheduled &&
                            hasDraftOrSubmitted &&
                            css`
                                color: #acacac;
                            `
                    ]}
                >
                    {dayjs
                        .unix(getShiftCurrentTime(earliestShift).start)
                        .format("H:mm")}
                    -
                </Typography>
            )}
        </div>
    )
}

const CalendarCells = ({
    date,
    renderCell: RenderCell,
    onClick,
    disableScheduledColor
}: {
    date: dayjs.Dayjs
    renderCell: (props: {
        date: dayjs.Dayjs
        disableScheduledColor?: boolean
    }) => React.ReactElement
    onClick: (date: dayjs.Dayjs) => void
    disableScheduledColor?: boolean
}) => {
    const firstDay = dayjs(date).startOf("month")
    const lastDay = dayjs(date).endOf("month")
    const firstDayOfWeek = firstDay.day()
    const lastDayOfWeek = lastDay.day()

    const datesOfFirstWeek = 7 - firstDayOfWeek
    const datesOfOtherWeeks =
        lastDay.date() - firstDay.date() - datesOfFirstWeek - lastDayOfWeek

    return (
        <>
            {Array.from({ length: 7 }).map((_, index) => (
                <div key={index}>
                    {index < firstDayOfWeek ? (
                        <PlainCell />
                    ) : (
                        <button
                            css={clickableCellStyle}
                            onClick={() =>
                                onClick(
                                    date.set("date", index - firstDayOfWeek + 1)
                                )
                            }
                        >
                            <RenderCell
                                date={date.set(
                                    "date",
                                    index - firstDayOfWeek + 1
                                )}
                                disableScheduledColor={disableScheduledColor}
                            />
                        </button>
                    )}
                </div>
            ))}
            {Array.from({
                length: datesOfOtherWeeks
            }).map((_, index) => (
                <div
                    key={index}
                    css={css`
                        border-top: 1px solid
                            ${theme.palette.intentions.cleanerGray.main};
                    `}
                >
                    <button
                        css={clickableCellStyle}
                        onClick={() =>
                            onClick(
                                date.set("date", index + datesOfFirstWeek + 1)
                            )
                        }
                    >
                        <RenderCell
                            date={date.set(
                                "date",
                                index + datesOfFirstWeek + 1
                            )}
                            disableScheduledColor={disableScheduledColor}
                        />
                    </button>
                </div>
            ))}
            {Array.from({ length: 7 }).map((_, index) => (
                <div
                    key={index}
                    css={css`
                        border-top: 1px solid
                            ${theme.palette.intentions.cleanerGray.main};
                    `}
                >
                    {index > lastDayOfWeek ? (
                        <PlainCell key={index} />
                    ) : (
                        <button
                            css={clickableCellStyle}
                            onClick={() =>
                                onClick(
                                    date.set(
                                        "date",
                                        datesOfFirstWeek +
                                            datesOfOtherWeeks +
                                            index +
                                            1
                                    )
                                )
                            }
                        >
                            <RenderCell
                                key={index}
                                date={date.set(
                                    "date",
                                    datesOfFirstWeek +
                                        datesOfOtherWeeks +
                                        index +
                                        1
                                )}
                                disableScheduledColor={disableScheduledColor}
                            />
                        </button>
                    )}
                </div>
            ))}
        </>
    )
}

export const ShiftCalendarPageUI = ({
    date,
    onChangeDate,
    shifts,
    refetch,
    renderCalendarControls: RenderCalendarControls,
    renderContent: RenderContent,
    renderFooter: RenderFooter,
    renderFooterContainer: RenderFooterContainer,
    disableScheduledColor,
    shouldRenderContentFullHeight
}: {
    date: dayjs.Dayjs
    onChangeDate: (date: dayjs.Dayjs) => void
    shifts?: Shift[]
    refetch?: () => void
    renderCalendarControls?: () => React.ReactElement
    renderContent?: ({ date }: { date: dayjs.Dayjs }) => React.ReactElement
    renderFooter?: () => React.ReactElement
    /**
     * @params renderFooterとrenderFooterContainerはいずれか一方しか指定できません。
     */
    renderFooterContainer?: () => React.ReactElement
    disableScheduledColor?: boolean
    shouldRenderContentFullHeight?: boolean
}) => {
    const { t } = useTranslation()

    const shiftMap = useMemo(
        () =>
            shifts?.reduce<Record<string, Shift[]>>((acc, current) => {
                return {
                    ...acc,
                    [dayjs
                        .unix(getShiftCurrentTime(current).start)
                        .format("YYYYMMDD")]: [
                        ...(acc[
                            dayjs
                                .unix(getShiftCurrentTime(current).start)
                                .format("YYYYMMDD")
                        ] ?? []),
                        current
                    ]
                }
            }, {}),
        [shifts]
    )

    const value = useMemo(
        () => ({
            shiftMap,
            selectedDate: date,
            refetch
        }),
        [date, refetch, shiftMap]
    )

    return (
        <ShiftContext.Provider value={value}>
            <header
                css={css`
                    padding-top: env(safe-area-inset-top, 0px);
                    background-color: ${
                        theme.palette.intentions.cleanerBackground.main
                    };
                    border-bottom: 1px solid
                        ${theme.palette.intentions.cleanerGray.main};
                `}
            >
                <div
                    css={css`
                        display: grid;
                        align-items: center;
                        justify-content: flex-start;
                        height: 52px;
                    `}
                >
                    <MobileMenu />
                </div>
                <CompanyNotificationBanner />

                {!shouldRenderContentFullHeight && (
                    <div
                        css={css`
                            display: grid;
                            align-items: center;
                        `}
                    >
                        {RenderCalendarControls && (
                            <div
                                css={css`
                                    /* 汎用性の観点からここでheightを固定するのは微妙だが、ここを差し替えてページを遷移することがあるのでずれないようにするためにこれは必要な調整と考える。 */
                                    height: 44px;
                                `}
                            >
                                <RenderCalendarControls />
                            </div>
                        )}

                        <div
                            css={css`
                                display: grid;
                                grid-template-columns: repeat(7, 1fr);
                                justify-content: space-around;
                                padding: 0 16px;
                                font-size: 10px;
                                line-height: 24px;
                                text-align: center;
                            `}
                        >
                            {[
                                {
                                    label: t("日"),
                                    color: "#e57358"
                                },
                                {
                                    label: t("月")
                                },
                                {
                                    label: t("火")
                                },
                                {
                                    label: t("水")
                                },
                                {
                                    label: t("木")
                                },
                                {
                                    label: t("金")
                                },
                                {
                                    label: t("土"),
                                    color: "#64b5f6"
                                }
                            ].map(({ label, color }) => (
                                <span
                                    key={label}
                                    css={
                                        color &&
                                        css`
                                            color: ${color};
                                        `
                                    }
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </header>

            {!shouldRenderContentFullHeight && (
                <div
                    css={css`
                        display: grid;
                        grid-template-columns: repeat(7, 1fr);
                        justify-content: space-around;
                        padding: 0 16px;
                        text-align: center;
                        border-bottom: 1px solid
                            ${theme.palette.intentions.cleanerGray.main};
                    `}
                >
                    <CalendarCells
                        date={date}
                        onClick={onChangeDate}
                        renderCell={RenderShiftCell}
                        // disableScheduledColorはCalendarCells経由でRenderShiftCellに渡すより、Contextなどで配るべきかもしれない
                        disableScheduledColor={disableScheduledColor}
                    />
                </div>
            )}
            <div
                css={css`
                    /* padidng-bottomについては、footerがfixedのため最低でもfooter高さ分は余分に入れておく必要がある(重なってしまうため) */
                    padding-bottom: 120px;
                `}
            >
                {RenderContent && <RenderContent date={date} />}
            </div>

            {RenderFooter || RenderFooterContainer ? (
                <footer
                    css={css`
                        position: fixed;
                        bottom: 0;
                        left: 0;
                        display: grid;
                        width: 100%;
                    `}
                >
                    {RenderFooterContainer ? (
                        <RenderFooterContainer />
                    ) : (
                        <Paper
                            square
                            elevation={3}
                            css={css`
                                padding: 16px 8px 34px 8px;
                            `}
                        >
                            {RenderFooter ? <RenderFooter /> : null}
                        </Paper>
                    )}
                </footer>
            ) : null}
        </ShiftContext.Provider>
    )
}
